import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getTopPlayersByRating, formatPlayerData } from '../services/chessToolsService';
import TopPlayersTable from '../components/chess/TopPlayersTable';
import PlayerHistoryModal from '../components/chess/PlayerHistoryModal';
// import { 
//   saveWeeklySnapshot, 
//   getPreviousWeekSnapshot, 
//   calculateChanges, 
//   shouldCreateNewSnapshot 
// } from '../services/rankingsSnapshotService';

const TwoSevenZeroOnePage = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');

  // List of inactive players to filter out when showing active only
  const inactivePlayers = ['Kasparov', 'Kramnik'];

  // Check if we're on standalone domain
  const isStandaloneDomain = window.location.hostname.includes('2701chess.com') || window.location.hostname.includes('2700chess.com');

  // Fetch players data
  const fetchPlayers = async () => {
    try {
      setError(null);
      const data = await getTopPlayersByRating(200); // Get top 200 players
      const formattedData = data.map(formatPlayerData)
        .filter(player => player.rating >= 2701); // Only show 2701+ rated players
      
      // Disable Firebase operations for now to prevent errors
      // TODO: Re-enable when Firebase environment is properly configured
      
      // Set players without change tracking for now
      setPlayers(formattedData);
    } catch (err) {
      console.error('Error fetching players:', err);
      if (err.message && err.message.includes('fetch')) {
        setError('Unable to connect to chess rankings API. Please check your internet connection.');
      } else {
        setError('Failed to load player rankings. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
    // Auto-refresh every 12 hours
    const interval = setInterval(fetchPlayers, 12 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Get unique countries for filter
  const countries = [...new Set(players.map(p => p.country))].sort();

  // Filter players based on search and filters
  const filteredPlayers = players.filter(player => {
    const matchesSearch = player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         player.country.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = !showOnlyActive || !inactivePlayers.some(inactive => 
      player.name.toLowerCase().includes(inactive.toLowerCase())
    );
    const matchesCountry = !selectedCountry || player.country === selectedCountry;
    
    return matchesSearch && matchesActive && matchesCountry;
  });

  // Custom header for standalone domain
  const StandaloneHeader = () => (
    <header>
      <div className="container mx-auto px-4 py-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white">2701Chess.com</h1>
          <p className="text-sm text-gray-400">The world's most exclusive chess club.</p>
          <div className="text-sm font-bold mt-2" style={{ color: '#ffd700' }}>
            This page is a work in progress. Suggestions?{' '}
            <a 
              href="https://trendingchess.com/contact" 
              className="hover:opacity-80 transition-opacity font-bold"
              style={{ color: '#ffd700' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              →
            </a>
          </div>
        </div>
      </div>
    </header>
  );

  return (
    <>
      {isStandaloneDomain && <StandaloneHeader />}
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gray-900 text-gray-100"
      >
        <div className={isStandaloneDomain ? "px-4 py-8" : "container mx-auto px-4 py-8"}>
          {/* Page Title */}
          {!isStandaloneDomain && (
            <div className="mb-8 text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                2701Chess.com
              </h1>
              <p className="text-gray-400">
                The world's most exclusive chess club.
              </p>
              <div className="text-sm font-bold mt-4" style={{ color: '#ffd700' }}>
                This page is a work in progress. Suggestions?{' '}
                <a 
                  href="https://trendingchess.com/contact" 
                  className="hover:opacity-80 transition-opacity font-bold"
                  style={{ color: '#ffd700' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  →
                </a>
              </div>
            </div>
          )}

          {/* Filters and Search */}
          <div className="mb-6 space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <input
                type="text"
                placeholder="Search by name or country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 text-gray-100"
              />

              {/* Country Filter */}
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 text-gray-100"
              >
                <option value="">Country</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>

              {/* Active Players Toggle */}
              <button
                onClick={() => setShowOnlyActive(!showOnlyActive)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  showOnlyActive 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-gray-800 hover:bg-gray-700 border border-gray-700'
                }`}
              >
                {showOnlyActive ? 'Active Players' : 'All Players'}
              </button>

              {/* Refresh Button */}
              <button
                onClick={fetchPlayers}
                disabled={loading}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-700 rounded-lg text-red-400">
              {error}
            </div>
          )}

          {/* Players Table */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-2xl text-gray-400">Loading rankings...</div>
            </div>
          ) : (
            <TopPlayersTable 
              players={filteredPlayers}
              onPlayerClick={setSelectedPlayer}
            />
          )}
        </div>

        {/* Player History Modal */}
        {selectedPlayer && (
          <PlayerHistoryModal
            player={selectedPlayer}
            onClose={() => setSelectedPlayer(null)}
          />
        )}

        {/* Footer for standalone domain */}
        {isStandaloneDomain && (
          <footer className="bg-gray-800 border-t border-gray-700 mt-12">
            <div className="container mx-auto px-4 py-6 text-center text-gray-400">
              <p>
                Powered by{' '}
                <a
                  href="https://trendingchess.com"
                  className="text-blue-400 hover:text-blue-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  TrendingChess.com
                </a>
              </p>
            </div>
          </footer>
        )}
      </motion.div>
    </>
  );
};

export default TwoSevenZeroOnePage;